/**
 * FoodDetailCard Component
 * Comprehensive display of food information including nutrition, portions, branding, etc.
 * Designed to work both as standalone component and in modal dialogs
 */

import React, { useState, useMemo, useRef, useEffect } from "react";
import { Food, FoodNutrient, MeasuredPortion } from "../types/passio";
import { NutritionSummaryCard } from "./NutritionSummaryCard";
import { getEffectiveQuantity, getTotalWeightInGrams } from "../utils/nutritionCalculations";
import { getConcernCode, getConcernSeverity } from "../types/concernCodes";
import { NumberInput } from "./NumberInput";

interface FoodDetailCardProps {
    food: Food | any; // Allow flexibility for different food data structures
    variant?: "full" | "compact";
    className?: string;
    style?: React.CSSProperties;
}

export const FoodDetailCard: React.FC<FoodDetailCardProps> = ({
    food,
    variant = "full",
    className,
    style,
}) => {
    // Extract main ingredient data (for foods with ingredients array)
    const mainIngredient = food?.ingredients?.[0] || food;
    
    // Memoize nutrients to prevent dependency changes
    const nutrients = useMemo(() => {
        return mainIngredient?.nutrients || [];
    }, [mainIngredient?.nutrients]);
    
    const portions = mainIngredient?.portions || food?.portions || [];
    const branded = mainIngredient?.branded || food?.branded;

    // State for selected portion (default to 100 gram portion)
    const [selectedPortionIndex, setSelectedPortionIndex] = useState(() => {
        // Look for 100g portion (weight.unit = "g", weight.value = 1, suggestedQuantity includes 100)
        const hundredGramIndex = portions.findIndex(
            (p: MeasuredPortion) =>
                p.weight.unit === "g" &&
                p.weight.value === 1 &&
                p.suggestedQuantity?.includes(100)
        );
        return hundredGramIndex >= 0 ? hundredGramIndex : 0;
    });

    const selectedPortion = portions[selectedPortionIndex] || portions[0];

    // State for quantity spinner
    const [portionQuantity, setPortionQuantity] = useState(() => {
        const portion = portions[selectedPortionIndex] || portions[0];
        return portion ? getEffectiveQuantity(portion) : 1;
    });

    // Determine step size based on whether the effective quantity is a whole number
    const getStepSize = () => {
        const portion = portions[selectedPortionIndex] || portions[0];
        if (!portion) return 0.1;

        const effectiveQty = getEffectiveQuantity(portion);
        // If it's a whole number, use step of 1, otherwise use 0.1
        return Number.isInteger(effectiveQty) ? 1 : 0.1;
    };

    // Ref for scrollable portion container
    const portionScrollRef = useRef<HTMLDivElement>(null);

    // Auto-scroll selected portion into view
    useEffect(() => {
        if (portionScrollRef.current && selectedPortionIndex >= 0) {
            const selectedCard = portionScrollRef.current.children[
                selectedPortionIndex
            ] as HTMLElement;
            if (selectedCard) {
                selectedCard.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "center",
                });
            }
        }
    }, [selectedPortionIndex]);

    // Calculate nutrition values based on selected portion
    const calculatedNutrition = useMemo(() => {
        if (!selectedPortion || !nutrients.length) {
            return {
                calories: 0,
                protein: 0,
                carbs: 0,
                fat: 0,
                fiber: 0,
                allNutrients: [],
            };
        }

        // Use the current quantity from the spinner
        const portionWeight = selectedPortion.weight.value * portionQuantity;
        const scaleFactor = portionWeight / 100; // Since nutrients are per 100g

        // Helper to get nutrient value by shortName
        const getNutrientValue = (shortName: string) => {
            const nutrient = nutrients.find(
                (n: FoodNutrient) => n.nutrient?.shortName === shortName
            );
            return nutrient ? nutrient.amount * scaleFactor : 0;
        };

        // Get all nutrients and sort alphabetically
        const allNutrients = nutrients
            .filter(
                (n: FoodNutrient) => n.nutrient?.name && n.amount !== undefined
            )
            .map((n: FoodNutrient) => ({
                name: n.nutrient!.name!,
                amount: n.amount! * scaleFactor,
                unit: n.nutrient!.unit || "",
            }))
            .sort((a: { name: string }, b: { name: string }) => {
                // Put items starting with numbers at the end
                const aStartsWithNumber = /^\d/.test(a.name);
                const bStartsWithNumber = /^\d/.test(b.name);

                if (aStartsWithNumber && !bStartsWithNumber) return 1;
                if (!aStartsWithNumber && bStartsWithNumber) return -1;

                return a.name.localeCompare(b.name);
            });

        return {
            calories: getNutrientValue("calories"),
            protein: getNutrientValue("protein"),
            carbs: getNutrientValue("carbs"),
            fat: getNutrientValue("fat"),
            fiber: getNutrientValue("fiber"),
            allNutrients,
        };
    }, [selectedPortion, nutrients, portionQuantity]);

    // Helper to format nutrient names
    const formatNutrientName = (name: string) => {
        return name
            .split(/[_\s]+/)
            .map(
                (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(" ");
    };

    // Helper to format nutrient value with unit
    const formatNutrientValue = (amount: number, unit: string) => {
        // Round to 2 decimal places
        const rounded = Math.round(amount * 100) / 100;
        return `${rounded}${unit}`;
    };

    // Helper to round display values
    const roundDisplay = (value: number) => {
        return Math.round(value * 100) / 100;
    };

    // Early return if no food data
    if (!food) {
        return (
            <div className="p-4 text-gray-500 text-center">
                No food information available
            </div>
        );
    }

    // Base styles
    const containerStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        ...style,
    };

    const headerStyle: React.CSSProperties = {
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "white",
        padding: variant === "compact" ? "16px" : "20px",
    };

    const contentStyle: React.CSSProperties = {
        padding: variant === "compact" ? "16px" : "20px",
    };

    const sectionStyle: React.CSSProperties = {
        marginBottom: variant === "compact" ? "16px" : "20px",
    };

    const sectionTitleStyle: React.CSSProperties = {
        fontSize: variant === "compact" ? "14px" : "16px",
        fontWeight: "600",
        color: "#374151",
        marginBottom: "8px",
        paddingBottom: "4px",
        borderBottom: "2px solid #e5e7eb",
    };

    const portionCardStyle = (isSelected: boolean): React.CSSProperties => ({
        backgroundColor: isSelected ? "#dbeafe" : "#f9fafb",
        padding: "12px",
        borderRadius: "8px",
        border: isSelected ? "2px solid #3b82f6" : "1px solid #e5e7eb",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        minWidth: "100px",
        textAlign: "center",
        flexShrink: 0,
        position: "relative" as const,
        transform: isSelected ? "scale(1.02)" : "scale(1)",
        boxShadow: isSelected
            ? "0 4px 12px rgba(59, 130, 246, 0.15)"
            : "0 1px 3px rgba(0, 0, 0, 0.1)",
    });

    const nutrientListStyle: React.CSSProperties = {
        maxHeight: "200px",
        overflowY: "auto",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        padding: "8px",
        backgroundColor: "#f9fafb",
    };

    const nutrientItemStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        padding: "6px 0",
        borderBottom: "1px solid #e5e7eb",
    };

    const tagStyle: React.CSSProperties = {
        display: "inline-block",
        backgroundColor: "#dbeafe",
        color: "#1e40af",
        padding: "4px 8px",
        borderRadius: "6px",
        fontSize: "12px",
        fontWeight: "500",
        margin: "2px",
    };

    const getConcernStyle = (
        severity: "high" | "medium" | "low"
    ): React.CSSProperties => {
        const baseStyle: React.CSSProperties = {
            display: "inline-block",
            padding: "6px 10px",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            margin: "2px",
            lineHeight: "1.4",
            maxWidth: "100%",
            wordBreak: "break-word",
        };

        switch (severity) {
            case "high":
                return {
                    ...baseStyle,
                    backgroundColor: "#fee2e2",
                    color: "#991b1b",
                    border: "1px solid #fecaca",
                };
            case "medium":
                return {
                    ...baseStyle,
                    backgroundColor: "#fef3c7",
                    color: "#92400e",
                    border: "1px solid #fed7aa",
                };
            case "low":
                return {
                    ...baseStyle,
                    backgroundColor: "#e0f2fe",
                    color: "#0c4a6e",
                    border: "1px solid #bae6fd",
                };
            default:
                return {
                    ...baseStyle,
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                    border: "1px solid #d1d5db",
                };
        }
    };

    return (
        <>
            {/* Custom scrollbar styles */}
            <style>
                {`
                    .portion-scroller {
                        scrollbar-width: thin;
                        scrollbar-color: #cbd5e0 #f3f4f6;
                        padding-bottom: 12px !important;
                        overflow-x: scroll !important;
                    }
                    .portion-scroller::-webkit-scrollbar {
                        height: 8px;
                        -webkit-appearance: none;
                        appearance: none;
                        display: block !important;
                        background: #f3f4f6;
                    }
                    .portion-scroller::-webkit-scrollbar-track {
                        background: #f3f4f6;
                        border-radius: 4px;
                        margin-top: 4px;
                        -webkit-box-shadow: inset 0 0 1px rgba(0,0,0,0.1);
                    }
                    .portion-scroller::-webkit-scrollbar-thumb {
                        background: #cbd5e0;
                        border-radius: 4px;
                        border: 1px solid #f3f4f6;
                        -webkit-box-shadow: inset 0 0 1px rgba(0,0,0,0.1);
                    }
                    .portion-scroller::-webkit-scrollbar-thumb:hover {
                        background: #9ca3af;
                    }
                    .portion-scroller::-webkit-scrollbar-corner {
                        background: #f3f4f6;
                    }
                `}
            </style>
            <div className={className} style={containerStyle}>
                {/* Header */}
                <div style={headerStyle}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                        }}
                    >
                        {food.iconId && (
                            <div
                                style={{
                                    width: "48px",
                                    height: "48px",
                                    backgroundColor: "rgba(255,255,255,0.2)",
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "24px",
                                }}
                            >
                                🍽️
                            </div>
                        )}
                        <div style={{ flex: 1 }}>
                            <h3
                                style={{
                                    margin: 0,
                                    fontSize:
                                        variant === "compact" ? "18px" : "20px",
                                    fontWeight: "600",
                                    lineHeight: "1.2",
                                }}
                            >
                                {food.displayName || food.name}
                            </h3>
                            {branded?.owner && (
                                <p
                                    style={{
                                        margin: "4px 0 0 0",
                                        fontSize: "14px",
                                        opacity: 0.9,
                                    }}
                                >
                                    by {branded.owner}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div style={contentStyle}>
                    {/* Nutrition Summary using NutritionSummaryCard */}
                    {nutrients.length > 0 && (
                        <div style={sectionStyle}>
                            <NutritionSummaryCard
                                title={`Nutrition per ${portionQuantity} ${selectedPortion.name}`}
                                nutritionData={calculatedNutrition}
                                variant="compact"
                            />
                        </div>
                    )}

                    {/* All Nutrients - Scrollable List */}
                    {nutrients.length > 0 && variant === "full" && (
                        <div style={sectionStyle}>
                            <h4 style={sectionTitleStyle}>All Nutrients</h4>
                            <div style={nutrientListStyle}>
                                {calculatedNutrition.allNutrients.map(
                                    (
                                        nutrient: {
                                            name: string;
                                            amount: number;
                                            unit: string;
                                        },
                                        index: number
                                    ) => (
                                        <div
                                            key={index}
                                            style={{
                                                ...nutrientItemStyle,
                                                borderBottom:
                                                    index ===
                                                    calculatedNutrition
                                                        .allNutrients.length -
                                                        1
                                                        ? "none"
                                                        : "1px solid #e5e7eb",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    color: "#374151",
                                                    fontSize: "12px",
                                                }}
                                            >
                                                {formatNutrientName(
                                                    nutrient.name
                                                )}
                                            </span>
                                            <span
                                                style={{
                                                    color: "#6b7280",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                }}
                                            >
                                                {formatNutrientValue(
                                                    nutrient.amount,
                                                    nutrient.unit
                                                )}
                                            </span>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    )}

                    {/* Serving Sizes */}
                    {portions.length > 0 && (
                        <div style={sectionStyle}>
                            <h4 style={sectionTitleStyle}>
                                Available Portions
                            </h4>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    width: "100%",
                                    maxWidth: "100%",
                                    overflow: "hidden",
                                }}
                            >
                                <div
                                    style={{
                                        position: "relative",
                                        flex: 1,
                                        minWidth: 0,
                                        maxWidth: "calc(100% - 140px)",
                                    }}
                                >
                                    {/* Gradient fade-off for scroll indication */}
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            right: 0,
                                            bottom: 0,
                                            width: "20px",
                                            background:
                                                "linear-gradient(to right, transparent, rgba(249, 250, 251, 0.8))",
                                            pointerEvents: "none",
                                            zIndex: 1,
                                            borderRadius: "0 8px 8px 0",
                                        }}
                                    />
                                    <div
                                        ref={portionScrollRef}
                                        className="portion-scroller"
                                        style={{
                                            display: "flex",
                                            gap: "8px",
                                            paddingRight: "20px", // Space for gradient
                                            width: "100%",
                                            maxWidth: "100%",
                                        }}
                                    >
                                        {portions
                                            .slice(
                                                0,
                                                variant === "compact"
                                                    ? 4
                                                    : undefined
                                            )
                                            .map(
                                                (
                                                    portion: MeasuredPortion,
                                                    index: number
                                                ) => {
                                                    // Always show the base weight value, not multiplied by quantity
                                                    const baseWeight =
                                                        roundDisplay(
                                                            portion.weight.value
                                                        );

                                                    return (
                                                        <div
                                                            key={index}
                                                            style={portionCardStyle(
                                                                index ===
                                                                    selectedPortionIndex
                                                            )}
                                                            onClick={() => {
                                                                setSelectedPortionIndex(
                                                                    index
                                                                );
                                                                // Update quantity to the effective quantity of the selected portion
                                                                setPortionQuantity(
                                                                    getEffectiveQuantity(
                                                                        portion
                                                                    )
                                                                );
                                                            }}
                                                            onMouseEnter={(
                                                                e
                                                            ) => {
                                                                if (
                                                                    index !==
                                                                    selectedPortionIndex
                                                                ) {
                                                                    e.currentTarget.style.backgroundColor =
                                                                        "#e0e7ff";
                                                                }
                                                            }}
                                                            onMouseLeave={(
                                                                e
                                                            ) => {
                                                                if (
                                                                    index !==
                                                                    selectedPortionIndex
                                                                ) {
                                                                    e.currentTarget.style.backgroundColor =
                                                                        "#f9fafb";
                                                                }
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    fontSize:
                                                                        "12px",
                                                                    color: "#6b7280",
                                                                    fontWeight:
                                                                        "500",
                                                                }}
                                                            >
                                                                {portion.name}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    fontSize:
                                                                        "14px",
                                                                    color: "#1f2937",
                                                                    fontWeight:
                                                                        "600",
                                                                    marginTop:
                                                                        "2px",
                                                                }}
                                                            >
                                                                {baseWeight}{" "}
                                                                {
                                                                    portion
                                                                        .weight
                                                                        .unit
                                                                }
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                            )}
                                    </div>
                                </div>

                                {/* Quantity Spinner */}
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "4px",
                                        borderLeft: "1px solid #e5e7eb",
                                        paddingLeft: "12px",
                                        minWidth: "140px",
                                        maxWidth: "140px",
                                        flexShrink: 0,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                        }}
                                    >
                                        <label
                                            style={{
                                                fontSize: "12px",
                                                color: "#6b7280",
                                                fontWeight: "500",
                                            }}
                                        >
                                            Qty:
                                        </label>
                                        <NumberInput
                                            min={getStepSize()}
                                            value={portionQuantity}
                                            onChange={(val) => {
                                                if (!isNaN(val) && val > 0) {
                                                    setPortionQuantity(roundDisplay(val));
                                                }
                                            }}
                                        />
                                    </div>
                                    {/* Total weight display */}
                                    {selectedPortion && (
                                        <div
                                            style={{
                                                fontSize: "11px",
                                                color: "#6b7280",
                                                fontStyle: "italic",
                                                textAlign: "center",
                                                paddingLeft: "32px", // Align with input
                                            }}
                                        >
                                            {getTotalWeightInGrams(selectedPortion, portionQuantity).displayText}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Branded Information - Only show if exists */}
                    {branded &&
                        (branded.owner ||
                            branded.productCode ||
                            branded.ingredients ||
                            branded.country) && (
                            <div style={sectionStyle}>
                                <h4 style={sectionTitleStyle}>
                                    Product Information
                                </h4>
                                <div
                                    style={{
                                        backgroundColor: "#f0f9ff",
                                        borderRadius: "8px",
                                        padding: "16px",
                                        border: "1px solid #bae6fd",
                                    }}
                                >
                                    <div
                                        style={{ display: "grid", gap: "12px" }}
                                    >
                                        {branded.owner && (
                                            <div>
                                                <span
                                                    style={{
                                                        fontSize: "12px",
                                                        color: "#0369a1",
                                                        fontWeight: "600",
                                                        textTransform:
                                                            "uppercase",
                                                    }}
                                                >
                                                    Brand
                                                </span>
                                                <div
                                                    style={{
                                                        fontSize: "14px",
                                                        color: "#0c4a6e",
                                                        marginTop: "2px",
                                                    }}
                                                >
                                                    {branded.owner}
                                                </div>
                                            </div>
                                        )}

                                        {(branded.productCode ||
                                            branded.country) && (
                                            <div
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns:
                                                        "1fr 1fr",
                                                    gap: "12px",
                                                }}
                                            >
                                                {branded.productCode && (
                                                    <div>
                                                        <span
                                                            style={{
                                                                fontSize:
                                                                    "12px",
                                                                color: "#0369a1",
                                                                fontWeight:
                                                                    "600",
                                                                textTransform:
                                                                    "uppercase",
                                                            }}
                                                        >
                                                            Product Code
                                                        </span>
                                                        <div
                                                            style={{
                                                                fontSize:
                                                                    "14px",
                                                                color: "#0c4a6e",
                                                                marginTop:
                                                                    "2px",
                                                            }}
                                                        >
                                                            {
                                                                branded.productCode
                                                            }
                                                        </div>
                                                    </div>
                                                )}
                                                {branded.country && (
                                                    <div>
                                                        <span
                                                            style={{
                                                                fontSize:
                                                                    "12px",
                                                                color: "#0369a1",
                                                                fontWeight:
                                                                    "600",
                                                                textTransform:
                                                                    "uppercase",
                                                            }}
                                                        >
                                                            Country
                                                        </span>
                                                        <div
                                                            style={{
                                                                fontSize:
                                                                    "14px",
                                                                color: "#0c4a6e",
                                                                marginTop:
                                                                    "2px",
                                                            }}
                                                        >
                                                            {branded.country.replace(
                                                                "en:",
                                                                ""
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {branded.ingredients && (
                                            <div>
                                                <span
                                                    style={{
                                                        fontSize: "12px",
                                                        color: "#0369a1",
                                                        fontWeight: "600",
                                                        textTransform:
                                                            "uppercase",
                                                    }}
                                                >
                                                    Ingredients
                                                </span>
                                                <div
                                                    style={{
                                                        fontSize: "13px",
                                                        color: "#0c4a6e",
                                                        lineHeight: "1.5",
                                                        marginTop: "4px",
                                                        backgroundColor:
                                                            "rgba(255,255,255,0.5)",
                                                        padding: "8px",
                                                        borderRadius: "6px",
                                                    }}
                                                >
                                                    {branded.ingredients}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                    {/* Tags and Concerns */}
                    {(food.tags?.length > 0 || food.concerns?.length > 0) && (
                        <div style={sectionStyle}>
                            <h4 style={sectionTitleStyle}>
                                {food.tags?.length > 0 &&
                                food.concerns?.length > 0
                                    ? "Tags & Dietary Concerns"
                                    : food.tags?.length > 0
                                    ? "Tags"
                                    : "Dietary Concerns"}
                            </h4>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "8px",
                                }}
                            >
                                {/* Tags Section */}
                                {food.tags?.length > 0 && (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "4px",
                                        }}
                                    >
                                        {food.tags.map(
                                            (tag: string, index: number) => (
                                                <span
                                                    key={index}
                                                    style={tagStyle}
                                                >
                                                    {tag}
                                                </span>
                                            )
                                        )}
                                    </div>
                                )}

                                {/* Concerns Section */}
                                {food.concerns?.length > 0 && (
                                    <div>
                                        <div
                                            style={{
                                                fontSize: "13px",
                                                fontWeight: "600",
                                                color: "#374151",
                                                marginBottom: "6px",
                                            }}
                                        >
                                            Dietary Concerns
                                        </div>
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: "6px",
                                            }}
                                        >
                                            {food.concerns.map(
                                                (
                                                    concern: number,
                                                    index: number
                                                ) => {
                                                    const concernInfo =
                                                        getConcernCode(concern);
                                                    const severity =
                                                        getConcernSeverity(
                                                            concern
                                                        );

                                                    return (
                                                        <div
                                                            key={index}
                                                            style={getConcernStyle(
                                                                severity
                                                            )}
                                                        >
                                                            <div
                                                                style={{
                                                                    fontWeight:
                                                                        "600",
                                                                    marginBottom:
                                                                        "2px",
                                                                }}
                                                            >
                                                                ⚠️{" "}
                                                                <strong>
                                                                    {concern}
                                                                </strong>{" "}
                                                                -{" "}
                                                                {concernInfo?.name ||
                                                                    "Unknown Concern"}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    fontSize:
                                                                        "11px",
                                                                    opacity: 0.9,
                                                                }}
                                                            >
                                                                {concernInfo?.description ||
                                                                    "No description available"}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Technical Info - Updated with more useful info */}
                    {variant === "full" &&
                        (food.qualityScore || nutrients.length > 0) && (
                            <div style={sectionStyle}>
                                <h4 style={sectionTitleStyle}>
                                    Technical Information
                                </h4>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "repeat(auto-fit, minmax(150px, 1fr))",
                                        gap: "12px",
                                    }}
                                >
                                    {food.qualityScore && (
                                        <div
                                            style={{
                                                backgroundColor: "#f9fafb",
                                                padding: "12px",
                                                borderRadius: "8px",
                                                border: "1px solid #e5e7eb",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: "12px",
                                                    color: "#6b7280",
                                                    fontWeight: "500",
                                                    textTransform: "uppercase",
                                                }}
                                            >
                                                Quality Score
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: "16px",
                                                    color: "#1f2937",
                                                    fontWeight: "600",
                                                    marginTop: "2px",
                                                }}
                                            >
                                                {food.qualityScore}
                                            </div>
                                        </div>
                                    )}
                                    {nutrients.length > 0 && (
                                        <div
                                            style={{
                                                backgroundColor: "#f9fafb",
                                                padding: "12px",
                                                borderRadius: "8px",
                                                border: "1px solid #e5e7eb",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: "12px",
                                                    color: "#6b7280",
                                                    fontWeight: "500",
                                                    textTransform: "uppercase",
                                                }}
                                            >
                                                Total Nutrients
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: "16px",
                                                    color: "#1f2937",
                                                    fontWeight: "600",
                                                    marginTop: "2px",
                                                }}
                                            >
                                                {nutrients.length} tracked
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                    {/* Source Information */}
                    {variant === "full" && food.origin?.length > 0 && (
                        <div style={sectionStyle}>
                            <h4 style={sectionTitleStyle}>Data Sources</h4>
                            {food.origin.map((origin: any, index: number) => (
                                <div
                                    key={index}
                                    style={{
                                        backgroundColor: "#f0fdf4",
                                        padding: "12px",
                                        borderRadius: "8px",
                                        border: "1px solid #bbf7d0",
                                        marginBottom: "8px",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: "14px",
                                                color: "#065f46",
                                                fontWeight: "600",
                                            }}
                                        >
                                            {origin.source}
                                        </span>
                                        {origin.id && (
                                            <span
                                                style={{
                                                    fontSize: "12px",
                                                    color: "#059669",
                                                }}
                                            >
                                                ID: {origin.id}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* License */}
                    {variant === "full" && food.licenseCopy && (
                        <div
                            style={{
                                fontSize: "11px",
                                color: "#6b7280",
                                lineHeight: "1.4",
                                padding: "12px",
                                backgroundColor: "#f9fafb",
                                borderRadius: "6px",
                                border: "1px solid #e5e7eb",
                            }}
                        >
                            {food.licenseCopy}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
